import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, ReferenceDot
} from "recharts";
import { RefreshCw, TrendingUp, TrendingDown, Target, Clock, ArrowUpRight, ArrowDownRight } from "lucide-react";
import FreshnessDot from "./FreshnessDot";
import { enrichWithAnchoredVwap, latestAnchoredVwap } from "../utils/vwap";

const PriceTrendChart = ({ ticker, timeframe = '1d', timeframeChanges = {} }) => {
  const [chartData, setChartData] = useState([]);
  const [zones, setZones] = useState({});
  const [current, setCurrent] = useState({});
  const [signals, setSignals] = useState({});
  const [regression, setRegression] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [forecast, setForecast] = useState(null);

  const fetchTrendData = useCallback(async () => {
    if (!ticker) return;
    const LS_KEY = `sp_price_trend_${ticker}_${timeframe}`;

    // Serve cached payload IMMEDIATELY so the chart never goes blank.
    let hadCache = false;
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || "null");
      if (cached?.data?.chart_data) {
        setChartData(cached.data.chart_data);
        setZones(cached.data.zones || {});
        setCurrent(cached.data.current || {});
        setSignals(cached.data.signals || {});
        setRegression(cached.data.regression || {});
        setLastUpdate(new Date(cached.ts));
        hadCache = true;
      }
    } catch {}

    if (!hadCache) setLoading(true);
    setError(null);

    const TIMEOUTS = [30000, 45000, 60000];
    let lastErr = null;
    for (let attempt = 0; attempt < TIMEOUTS.length; attempt++) {
      try {
        const response = await axios.get(`${API}/stock/price-trend/${ticker}`, {
          params: { timeframe },
          timeout: TIMEOUTS[attempt],
        });
        if (response.data && response.data.chart_data) {
          setChartData(response.data.chart_data);
          setZones(response.data.zones || {});
          setCurrent(response.data.current || {});
          setSignals(response.data.signals || {});
          setRegression(response.data.regression || {});
          setLastUpdate(new Date());
          try { localStorage.setItem(LS_KEY, JSON.stringify({ data: response.data, ts: Date.now() })); } catch {}
          setLoading(false);
          return;
        } else if (response.data?.error) {
          setError(response.data.error);
          setLoading(false);
          return;
        }
      } catch (err) {
        lastErr = err;
        if (attempt < TIMEOUTS.length - 1) {
          await new Promise(r => setTimeout(r, (attempt + 1) * 1500));
        }
      }
    }
    // All attempts failed. If we already have cached chart data, stay silent.
    // Otherwise the JSX will render the skeleton via the error branch below.
    console.warn(`PriceTrend ${ticker}: silent fail, will keep cached data`, lastErr?.message);
    if (!hadCache) setError("Failed to load trend data. Please try again.");
    setLoading(false);
  }, [ticker, timeframe]);

  const fetchForecast = useCallback(async () => {
    if (!ticker) return;
    try {
      const res = await axios.get(`${API}/stock/chart-forecast/${ticker}`, {
        params: { timeframe },
        timeout: 90000,
      });
      if (res.data && !res.data.error) {
        setForecast(res.data);
      } else {
        setForecast(null);
      }
    } catch (err) {
      setForecast(null);
    }
  }, [ticker, timeframe]);

  useEffect(() => {
    fetchTrendData();
    const interval = setInterval(fetchTrendData, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [fetchTrendData]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  // Append forward forecast bars to the chart data + compute per-bar Anchored VWAP
  const { enrichedChart, forecastEndTime, forecastEndPrice, latestVwap } = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { enrichedChart: [], forecastEndTime: null, forecastEndPrice: null, latestVwap: null };
    }
    // Step 1 — anchor VWAP to the first bar of the visible period
    const withVwap = enrichWithAnchoredVwap(chartData);
    const lvwap = latestAnchoredVwap(withVwap);

    // Step 2 — append forecast bars (no real volume → VWAP stays flat at last value)
    const enriched = withVwap.map(d => ({ ...d, forecast: null }));
    if (!forecast?.forecast_bars?.length) {
      return { enrichedChart: enriched, forecastEndTime: null, forecastEndPrice: null, latestVwap: lvwap };
    }
    const last = enriched[enriched.length - 1];
    if (last) {
      enriched[enriched.length - 1] = { ...last, forecast: last.price };
    }
    forecast.forecast_bars.forEach(fb => {
      enriched.push({
        time: fb.time,
        price: null,
        regression: null,
        sma20: null,
        sma50: null,
        vwap: null,
        support: last?.support ?? null,
        resistance: last?.resistance ?? null,
        forecast: fb.forecast_price,
        isForecast: true,
      });
    });
    const lastFc = forecast.forecast_bars[forecast.forecast_bars.length - 1];
    return {
      enrichedChart: enriched,
      forecastEndTime: lastFc.time,
      forecastEndPrice: lastFc.forecast_price,
      latestVwap: lvwap,
    };
  }, [chartData, forecast]);

  const getTrendIcon = () => {
    if (current.trend === 'BULLISH') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (current.trend === 'BEARISH') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Target className="w-4 h-4 text-yellow-400" />;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-xs mb-2 font-medium">{label}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-purple-400">Price:</span>
              <span className="text-white font-bold">${data?.price?.toFixed(2)}</span>
            </div>
            {data?.regression && (
              <div className="flex justify-between gap-4">
                <span className="text-amber-400">Regression:</span>
                <span className="text-white">${data.regression?.toFixed(2)}</span>
              </div>
            )}
            {data?.sma20 && (
              <div className="flex justify-between gap-4">
                <span className="text-blue-400">SMA 20:</span>
                <span className="text-white">${data.sma20?.toFixed(2)}</span>
              </div>
            )}
            {data?.sma50 && (
              <div className="flex justify-between gap-4">
                <span className="text-cyan-400">SMA 50:</span>
                <span className="text-white">${data.sma50?.toFixed(2)}</span>
              </div>
            )}
            {data?.vwap != null && (
              <div className="flex justify-between gap-4">
                <span className="text-fuchsia-400">VWAP (anchored):</span>
                <span className="text-white">${data.vwap?.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between gap-4 pt-1 border-t border-gray-700">
              <span className="text-green-400">Support:</span>
              <span className="text-white">${data?.support?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-red-400">Resistance:</span>
              <span className="text-white">${data?.resistance?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate Y-axis domain
  const getYDomain = () => {
    if (!chartData.length) return [0, 100];
    const allPrices = chartData.flatMap(d => [d.price, d.high, d.low, d.sma20, d.sma50, d.regression]).filter(Boolean);
    if (latestVwap != null) allPrices.push(latestVwap);
    const min = Math.min(...allPrices);
    const max = Math.max(...allPrices);
    const padding = (max - min) * 0.1;
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  };

  const timeframeLabel = {
    '1d': '1 Day',
    '5d': '5 Days',
    '1mo': '1 Month',
    '3mo': '3 Months',
    '6mo': '6 Months',
    '1y': '1 Year'
  }[timeframe] || timeframe;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Target className="w-5 h-5 text-purple-400" />
          <h3 className="text-base sm:text-lg font-semibold text-white">
            {ticker} Price Trend
          </h3>
          <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded">{timeframeLabel}</span>
          <FreshnessDot timestamp={lastUpdate} size="sm" showLabel={true} />
          {regression.trend && (
            <span 
              className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium ${
                regression.trend === 'BULLISH' ? 'bg-green-900/50 text-green-400' :
                regression.trend === 'BEARISH' ? 'bg-red-900/50 text-red-400' :
                'bg-yellow-900/50 text-yellow-400'
              }`}
            >
              {regression.trend === 'BULLISH' ? <TrendingUp className="w-3 h-3" /> :
               regression.trend === 'BEARISH' ? <TrendingDown className="w-3 h-3" /> :
               <Target className="w-3 h-3" />}
              {regression.trend}
              <span className="opacity-70">
                ({regression.slope_pct >= 0 ? '+' : ''}{regression.slope_pct?.toFixed(2)}%)
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchTrendData}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-xs text-white transition-colors"
            data-testid="refresh-price-trend"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Signal Cards - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {/* Current Price */}
        <div className={`bg-[#111] border rounded-lg p-2 sm:p-3 ${
          current.price_change_pct >= 0 ? 'border-green-900/50' : 'border-red-900/50'
        }`}>
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            Current Price
            {current.price_change_pct >= 0 ? 
              <ArrowUpRight className="w-3 h-3 text-green-400" /> : 
              <ArrowDownRight className="w-3 h-3 text-red-400" />
            }
          </div>
          <div className={`text-lg font-bold ${current.price_change_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${current.price?.toFixed(2) || '--'}
          </div>
          <div className="text-xs text-gray-500">
            {current.price_change_pct >= 0 ? '+' : ''}{current.price_change_pct?.toFixed(2)}%
          </div>
        </div>
        
        {/* Entry Zone */}
        <div className={`bg-[#111] border rounded-lg p-2 sm:p-3 ${
          signals.near_support ? 'border-green-500 bg-green-900/10' : 'border-gray-800'
        }`}>
          <div className="text-xs text-green-500 mb-1 flex items-center gap-1">
            <ArrowUpRight className="w-3 h-3" />
            Entry Zone
          </div>
          <div className="text-sm font-bold text-green-400">
            ${zones.entry_zone?.low?.toFixed(2)} - ${zones.entry_zone?.high?.toFixed(2)}
          </div>
          {signals.near_support && (
            <div className="text-xs text-green-400 mt-1 font-medium animate-pulse">⬆ Near Entry!</div>
          )}
        </div>
        
        {/* Exit Zone */}
        <div className={`bg-[#111] border rounded-lg p-2 sm:p-3 ${
          signals.near_resistance ? 'border-red-500 bg-red-900/10' : 'border-gray-800'
        }`}>
          <div className="text-xs text-red-500 mb-1 flex items-center gap-1">
            <ArrowDownRight className="w-3 h-3" />
            Exit Zone
          </div>
          <div className="text-sm font-bold text-red-400">
            ${zones.exit_zone?.low?.toFixed(2)} - ${zones.exit_zone?.high?.toFixed(2)}
          </div>
          {signals.near_resistance && (
            <div className="text-xs text-red-400 mt-1 font-medium animate-pulse">⬇ Near Exit!</div>
          )}
        </div>
        
        {/* Support/Resistance */}
        <div className="bg-[#111] border border-gray-800 rounded-lg p-2 sm:p-3">
          <div className="text-xs text-gray-500 mb-1">Key Levels</div>
          <div className="flex flex-col gap-1">
            <div className="flex justify-between text-xs">
              <span className="text-red-400">Resistance:</span>
              <span className="text-white font-medium">${zones.resistance?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-green-400">Support:</span>
              <span className="text-white font-medium">${zones.support?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* R² Trend Strength */}
        <div className="bg-[#111] border border-gray-800 rounded-lg p-2 sm:p-3">
          <div className="text-xs text-gray-500 mb-1">Trend Strength</div>
          <div className={`text-lg font-bold ${
            regression.r_squared >= 0.7 ? 'text-green-400' :
            regression.r_squared >= 0.5 ? 'text-yellow-400' : 'text-gray-400'
          }`}>
            {((regression.r_squared || 0) * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-gray-500">R² Correlation</div>
          <div className={`text-[10px] mt-1 ${
            regression.r_squared >= 0.7 ? 'text-green-500' :
            regression.r_squared >= 0.5 ? 'text-yellow-500' : 'text-gray-500'
          }`}>
            {regression.r_squared >= 0.7 ? 'Strong trend — price closely follows the regression line' :
             regression.r_squared >= 0.5 ? 'Moderate trend — price loosely follows the regression line' :
             regression.r_squared >= 0.3 ? 'Weak trend — price deviates significantly from the regression line' :
             'No clear trend — price movement is mostly random'}
          </div>
        </div>

        {/* Period Gain/Loss */}
        {timeframeChanges[timeframe] !== undefined && (
          <div className={`bg-[#111] border rounded-lg p-2 sm:p-3 ${
            timeframeChanges[timeframe] >= 0 ? 'border-green-900/50' : 'border-red-900/50'
          }`}>
            <div className="text-xs text-gray-500 mb-1">Period Change</div>
            <div className={`text-lg font-bold ${
              timeframeChanges[timeframe] >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {timeframeChanges[timeframe] >= 0 ? '+' : ''}{timeframeChanges[timeframe]?.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500">Gain/Loss</div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="bg-[#111] border border-gray-800 rounded-lg p-3" data-testid="price-trend-chart">
        {loading && chartData.length === 0 ? (
          <div className="h-[350px] flex items-center justify-center">
            <div className="text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Loading price trend data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="h-[350px] flex items-center justify-center" data-testid="price-trend-skeleton">
            <div className="text-center max-w-sm">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mb-2" />
              <p className="text-sm text-amber-300" data-testid="price-trend-unavailable">Price trend data temporarily unavailable</p>
              <p className="text-[11px] text-gray-500 mt-1">{error}</p>
              <button data-testid="price-trend-retry" onClick={() => fetchTrendData()} className="mt-3 px-3 py-1 rounded border border-gray-700 text-xs text-gray-300 hover:text-white hover:border-gray-500">Try again</button>
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[350px] flex items-center justify-center">
            <p className="text-gray-500 text-sm">No trend data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={enrichedChart || chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="time" 
                stroke="#64748b" 
                tick={{ fill: '#94a3b8', fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke="#64748b" 
                tick={{ fill: '#94a3b8', fontSize: 10 }}
                domain={getYDomain()}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Entry Zone (Green shaded area) */}
              {zones.entry_zone && (
                <ReferenceArea 
                  y1={zones.entry_zone.low} 
                  y2={zones.entry_zone.high} 
                  fill="#22c55e" 
                  fillOpacity={0.1}
                  stroke="#22c55e"
                  strokeDasharray="3 3"
                  strokeOpacity={0.5}
                />
              )}
              
              {/* Exit Zone (Red shaded area) */}
              {zones.exit_zone && (
                <ReferenceArea 
                  y1={zones.exit_zone.low} 
                  y2={zones.exit_zone.high} 
                  fill="#ef4444" 
                  fillOpacity={0.1}
                  stroke="#ef4444"
                  strokeDasharray="3 3"
                  strokeOpacity={0.5}
                />
              )}
              
              {/* Support Line */}
              {zones.support && (
                <ReferenceLine 
                  y={zones.support} 
                  stroke="#22c55e" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  label={{ value: `Support $${zones.support}`, fill: '#22c55e', fontSize: 10, position: 'left' }}
                />
              )}
              
              {/* Resistance Line */}
              {zones.resistance && (
                <ReferenceLine 
                  y={zones.resistance} 
                  stroke="#ef4444" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  label={{ value: `Resistance $${zones.resistance}`, fill: '#ef4444', fontSize: 10, position: 'left' }}
                />
              )}

              {/* Anchored VWAP — violet per-bar line, accumulates from period start */}
              <Line
                type="monotone"
                dataKey="vwap"
                stroke="#ee82ee"
                strokeWidth={2.5}
                strokeDasharray="4 3"
                dot={false}
                connectNulls
                name="VWAP (anchored)"
                isAnimationActive={false}
              />
              
              {/* SMA 50 Line */}
              <Line 
                type="monotone" 
                dataKey="sma50" 
                stroke="#06b6d4" 
                strokeWidth={1.5} 
                dot={false}
                name="SMA 50"
                strokeDasharray="3 3"
              />
              
              {/* SMA 20 Line */}
              <Line 
                type="monotone" 
                dataKey="sma20" 
                stroke="#3b82f6" 
                strokeWidth={1.5} 
                dot={false}
                name="SMA 20"
              />
              
              {/* Regression Line */}
              <Line 
                type="monotone" 
                dataKey="regression" 
                stroke="#f59e0b" 
                strokeWidth={2.5} 
                dot={false}
                name="Regression"
                strokeDasharray="8 4"
              />
              
              {/* Price Line */}
              <Line 
                type="monotone" 
                dataKey="price" 
                stroke="#a855f7" 
                strokeWidth={2.5} 
                dot={false}
                name="Price"
              />

              {/* FORECAST Line — cyan dashed, projects forward for this timeframe */}
              <Line
                type="linear"
                dataKey="forecast"
                stroke="#22d3ee"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                strokeOpacity={0.95}
                dot={false}
                connectNulls
                isAnimationActive={false}
                name="Forecast"
              />

              {/* Floating Est. Close $ label at the end of the forecast */}
              {forecast && forecastEndTime && forecastEndPrice && (
                <ReferenceDot
                  x={forecastEndTime}
                  y={forecastEndPrice}
                  r={4}
                  fill="#22d3ee"
                  stroke="#0e7490"
                  strokeWidth={1.5}
                  isFront={true}
                  label={{
                    value: (() => {
                      const conf = forecast.confidence_pct;
                      const band = forecast.confidence_band_pct;
                      if (conf !== undefined && conf !== null) {
                        return `Est: $${forecastEndPrice.toFixed(2)} • ${conf}% conf (±${band}%)`;
                      }
                      return `Est: $${forecastEndPrice.toFixed(2)}`;
                    })(),
                    position: 'left',
                    offset: 8,
                    fill: '#67e8f9',
                    fontSize: 10,
                    fontWeight: 'bold',
                  }}
                />
              )}
              
              <Legend 
                verticalAlign="top"
                height={36}
                formatter={(value) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{value}</span>}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Forecast Info Bar — above the legend */}
      {forecast && forecast.est_close_price && forecast.total_samples > 0 && (
        <div
          data-testid="price-trend-forecast-info-bar"
          className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 rounded-md border border-cyan-900/40 bg-gradient-to-r from-cyan-950/30 via-gray-900/40 to-gray-900/40 text-[11px]"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-6 h-0.5 rounded" style={{ borderTop: '2.5px dashed #22d3ee' }}></span>
            <span className="text-cyan-300 font-semibold tracking-wide uppercase">{timeframe} Forecast</span>
            <span className={`px-1.5 py-0.5 rounded font-mono ${
              forecast.est_close_pct > 0.1 ? 'bg-green-900/50 text-green-300'
              : forecast.est_close_pct < -0.1 ? 'bg-red-900/50 text-red-300'
              : 'bg-gray-800 text-gray-300'
            }`}>
              {forecast.est_close_pct >= 0 ? '+' : ''}{forecast.est_close_pct.toFixed(2)}%
            </span>
            <span className="ml-1 px-2 py-0.5 rounded border border-green-800/60 bg-green-950/40 flex items-center gap-1">
              <span className="text-gray-400 uppercase tracking-wide text-[10px]">Est. Close:</span>
              <span className={`font-mono font-bold ${forecast.est_close_pct >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                ${forecast.est_close_price.toFixed(2)}
              </span>
              <span
                className={`ml-1 px-1.5 py-0.5 rounded font-mono text-[10px] font-bold ${
                  forecast.confidence_pct >= 70 ? 'bg-green-800/60 text-green-200'
                  : forecast.confidence_pct >= 40 ? 'bg-yellow-800/60 text-yellow-200'
                  : 'bg-red-800/60 text-red-200'
                }`}
                title={`${forecast.confidence_pct}% of backtested ${forecast.horizon_bars}-bar forecasts landed within ±${forecast.confidence_band_pct}% of projection over ${forecast.history_period} of history`}
              >
                {forecast.confidence_pct}% conf (±{forecast.confidence_band_pct}%)
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <span className="uppercase tracking-wide text-[10px]">{forecast.history_period} Backtest:</span>
            <span>Direction <span className={`font-mono font-bold ${
              forecast.direction_accuracy_pct >= 55 ? 'text-green-400'
              : forecast.direction_accuracy_pct >= 50 ? 'text-yellow-400'
              : 'text-red-400'
            }`}>{forecast.direction_accuracy_pct}%</span></span>
            <span className="text-gray-700">|</span>
            <span>Avg Err <span className="font-mono text-gray-300">±{forecast.avg_abs_error_pct}%</span></span>
            <span className="text-gray-700">|</span>
            <span className="font-mono">{forecast.total_samples.toLocaleString()} samples</span>
          </div>
        </div>
      )}

      {/* Legend / Key */}
      <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <div className="w-8 h-3 bg-green-500/20 border border-green-500 border-dashed rounded" />
          <span>Entry Zone</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-3 bg-red-500/20 border border-red-500 border-dashed rounded" />
          <span>Exit Zone</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-purple-500" />
          <span>Price</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-amber-500 border-dashed" />
          <span>Regression</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-fuchsia-400 border-dashed" />
          <span>VWAP (anchored)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-cyan-400 border-dashed" />
          <span>Forecast</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-blue-500" />
          <span>SMA 20</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-cyan-500 border-dashed" />
          <span>SMA 50</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-green-500 border-dashed" />
          <span>Support</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 border-t-2 border-red-500 border-dashed" />
          <span>Resistance</span>
        </div>
      </div>
    </div>
  );
};

export default PriceTrendChart;
